import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import JobServiceForm from "../../components/service/jobServiceForm.component";
import { useCRUD } from "../../api/crud";
import { JOB_SERVICES_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";

export const JobServiceCatalogDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(JOB_SERVICES_ENDPOINT);
  const [updateOpened, setUpdateOpened] = useState<boolean>(false);
  const [deleteOpened, setDeleteOpened] = useState<boolean>(false);
  const canChange = useHasPermission("service.change_jobservice");
  const canDelete = useHasPermission("service.delete_jobservice");

  const handleDelete = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Service deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteOpened(false);
      navigate('/service/services-catalog');
    } else {
      enqueueSnackbar(t('Failed to delete service'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update service"),
      onClick: () => setUpdateOpened(true),
      disabled: !canChange,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete service"),
      onClick: () => setDeleteOpened(true),
      color: "var(--errorColor)",
      disabled: !canDelete,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('If this service is not used in any job, it will be permanently removed from the catalog.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('If this service is already assigned to a job, it will be deactivated instead: it stays visible in job history but will no longer appear in forms or the catalog list.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDelete}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateOpened}
        setOpened={setUpdateOpened}
        title={t("Update service")}
        content={
          <JobServiceForm
            id={id}
            onSuccess={() => {
              setUpdateOpened(false);
              window.dispatchEvent(new Event("jobServiceCatalogUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t("Delete service")}
        content={deleteModalContent}
      />
    </>
  );
};
